import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Image as ImageIcon,
  Settings,
  Sliders,
  RotateCw,
  FlipHorizontal,
  Sun,
  Contrast,
  Shuffle,
  Crop,
  Palette,
  Volume2,
  Save, Eye,
  RefreshCw,
  Target,
  CheckCircle2,
  AlertCircle,
  Maximize, Scissors,
  Filter,
  Camera,
  Droplet,
  X,
  Check
} from "lucide-react";
import { message, Spin, Switch, Slider, Select, InputNumber, Modal } from "antd";
import axios from "../../utils/axios";

const { Option } = Select;

const DatasetVersion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const organizationId = localStorage.getItem("organizationId");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentTransformType, setCurrentTransformType] = useState(null);
  const [tempSettings, setTempSettings] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Preprocessing states
  const [preprocessing, setPreprocessing] = useState({
    autoOrient: { enabled: false, method: 'exif' },
    resizing: { enabled: false, width: 224, height: 224, maintainAspect: true },
    isolationObject: { enabled: false, method: 'background_removal', threshold: 0.5 },
    staticCrop: { enabled: false, x: 0, y: 0, width: 224, height: 224 },
    dynamicCrop: { enabled: false, method: 'center', padding: 0.1 },
    grayscale: { enabled: false, method: 'luminance' },
    // autoEdgeContract: { enabled: false, strength: 1.0, threshold: 0.1 }
    autoEdgeContract: {
      enabled: false,
      method: 'contrast_stretching', // Default method
      fixedSize: 640, // Default values, will be set based on method
      strength: 1.5,
      threshold: 0.1,
    },
    
  });

  // Augmentation states
  const [augmentation, setAugmentation] = useState({
    flip: { enabled: false, horizontal: true, vertical: false, probability: 0.5 },
    crop: { enabled: false, size: 0.8, random: true, aspectRatio: 'preserve' },
    rotation: { enabled: false, angle: 15, random: true },
    blur: { enabled: false, type: 'gaussian', radius: 1.0, probability: 0.3 },
    grayscale: { enabled: false, probability: 0.2 },
    saturation: { enabled: false, range: [0.8, 1.2], probability: 0.5 },
    brightness: { enabled: false, range: [0.8, 1.2], probability: 0.5 },
    exposure: { enabled: false, range: [0.8, 1.2], probability: 0.4 },
    noise: { enabled: false, intensity: 1, probability: 0.3 },
    shear: { enabled: false, horizontal: 0, vertical: 0, probability: 0.5 }
  });

  const [versionDescription, setVersionDescription] = useState('');

  useEffect(() => {
    if (!location.state?.selectedProject || !location.state.selectedProject.images) {
      message.error('No valid project selected. Redirecting to annotations.');
      navigate('/sidebar/ViewAnnotation');
      return;
    }
    setSelectedProject(location.state.selectedProject);
  }, [location.state, navigate]);

  const openPreviewModal = (type, category, currentSettings) => {
    if (type === 'autoEdgeContract') {
      const newSettings = { ...currentSettings };
      // Ensure settings are initialized based on method
      switch (newSettings.method) {
        case 'contrast_stretching':
          newSettings.fixedSize = 640;
          newSettings.strength = 1.5;
          newSettings.threshold = 0.1;
          break;
        case 'histogram_equalization':
          newSettings.fixedSize = 640;
          newSettings.strength = 1.3;
          newSettings.threshold = 0.15;
          break;
        case 'adaptive_equalization':
          newSettings.fixedSize = 640;
          newSettings.strength = 1.7;
          newSettings.threshold = 0.2;
          break;
        default:
          newSettings.method = 'contrast_stretching';
          newSettings.fixedSize = 400;
          newSettings.strength = 1.5;
          newSettings.threshold = 0.1;
      }
      setTempSettings(newSettings);
    } else {
      setTempSettings({ ...currentSettings });
    }
    setCurrentTransformType({ type, category });
    setIsModalVisible(true);
    setImageLoadError(null);
  };

  const applyPreviewEffect = (canvas, img, type, settings) => {
    if (!canvas || !img) {
      setImageLoadError('Canvas or image is missing');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setImageLoadError('Failed to get canvas context');
      return;
    }
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set canvas size to match image
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    // Apply different effects based on type
    switch (type) {
      case 'flip':
        ctx.save();
        if (settings.horizontal && settings.vertical) {
          ctx.scale(-1, -1);
          ctx.drawImage(img, -canvas.width, -canvas.height, canvas.width, canvas.height);
        } else if (settings.horizontal) {
          ctx.scale(-1, 1);
          ctx.drawImage(img, -canvas.width, 0, canvas.width, canvas.height);
        } else if (settings.vertical) {
          ctx.scale(1, -1);
          ctx.drawImage(img, 0, -canvas.height, canvas.width, canvas.height);
        } else {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }
        ctx.restore();
        break;

      case 'rotation':
        ctx.save();
        const angle = settings.angle * (Math.PI / 180);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(angle);
        ctx.drawImage(img, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
        ctx.restore();
        break;

      case 'crop':
        const cropSize = settings.size;
        const cropWidth = canvas.width * cropSize;
        const cropHeight = canvas.height * cropSize;
        const cropX = (canvas.width - cropWidth) / 2;
        const cropY = (canvas.height - cropHeight) / 2;
        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
        break;

      case 'brightness':
        ctx.filter = `brightness(${settings.range[1]})`;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        break;

      case 'saturation':
        ctx.filter = `saturate(${settings.range[1]})`;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        break;

      case 'grayscale':
        ctx.filter = 'grayscale(100%)';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        break;

      case 'blur':
        ctx.filter = `blur(${settings.radius}px)`;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        break;

      case 'exposure':
        ctx.filter = `brightness(${settings.range[1]}) contrast(${settings.range[1]})`;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        break;

      case 'resizing':
        canvas.width = settings.width;
        canvas.height = settings.height;
        ctx.drawImage(img, 0, 0, settings.width, settings.height);
        break;

case 'noise': {

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const intensity = settings.intensity || 1;

  const noiseCanvas = document.createElement('canvas');
  noiseCanvas.width = canvas.width;
  noiseCanvas.height = canvas.height;

  const noiseCtx = noiseCanvas.getContext('2d');

  const pixels = canvas.width * canvas.height * intensity * 0.4;

  for (let i = 0; i < pixels; i++) {

    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;

    const gray = Math.floor(Math.random() * 255);

    noiseCtx.fillStyle = `rgba(${gray},${gray},${gray},0.25)`;
    noiseCtx.fillRect(x, y, 1, 1);
  }

  ctx.drawImage(noiseCanvas, 0, 0);

  break;
}

      case 'isolationObject':
      case 'staticCrop':
      case 'dynamicCrop':

      case 'autoEdgeContract': {

        let fixedSize = 400;

        const scale = fixedSize / Math.max(img.naturalWidth, img.naturalHeight);
        const newWidth = img.naturalWidth * scale;
        const newHeight = img.naturalHeight * scale;
        const padX = (fixedSize - newWidth) / 2;
        const padY = (fixedSize - newHeight) / 2;

        canvas.width = fixedSize;
        canvas.height = fixedSize;

        ctx.fillStyle = "black";
        ctx.fillRect(0, 0, fixedSize, fixedSize);

        if (settings.method === "contrast_stretching") {
          ctx.filter = "contrast(1.4) saturate(1.1)";
        }
        else if (settings.method === "histogram_equalization") {
          ctx.filter = "contrast(1.6) brightness(1.1) saturate(1.15)";
        }
        else if (settings.method === "adaptive_equalization") {
          ctx.filter = "contrast(1.8) brightness(1.15) saturate(1.2)";
        }

        ctx.drawImage(img, padX, padY, newWidth, newHeight);

        ctx.filter = "none";

        break;
      }
      case 'autoOrient':
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        break;
      
      case "shear": {

        const w = canvas.width;
        const h = canvas.height;

        const halfW = w / 2;
        const halfH = h / 2;

        const shearScale = 0.35; // Roboflow-like intensity

        const shearX = Math.tan((settings.horizontal * Math.PI) / 180) * shearScale;
        const shearY = Math.tan((settings.vertical * Math.PI) / 180) * shearScale;

        const variants = [
          { sx: shearX, sy: shearY },
          { sx: shearX, sy: -shearY },
          { sx: -shearX, sy: shearY },
          { sx: -shearX, sy: -shearY },
        ];

        ctx.clearRect(0,0,w,h);
        ctx.fillStyle="#f8fafc";
        ctx.fillRect(0,0,w,h);

        variants.forEach((v,i)=>{

          const col = i % 2;
          const row = Math.floor(i/2);

          const x = col * halfW;
          const y = row * halfH;

          ctx.save();

          ctx.beginPath();
          ctx.rect(x,y,halfW,halfH);
          ctx.clip();

          ctx.translate(x,y);

          // FIXED ORDER
          ctx.transform(
            1,
            v.sx,
            v.sy,
            1,
            0,
            0
          );

          ctx.drawImage(img,0,0,halfW,halfH);

          ctx.restore();

        });

        break;
      }

      default:
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
  };

  const handleApplyTransform = () => {
    if (!currentTransformType || !tempSettings) {
      console.warn('Cannot apply transform: missing transform type or settings');
      return;
    }

    const { type, category } = currentTransformType;

    if (category === 'preprocessing') {
      setPreprocessing(prev => ({
        ...prev,
        [type]: { ...tempSettings, enabled: true }
      }));
    } else {
      setAugmentation(prev => ({
        ...prev,
        [type]: { ...tempSettings, enabled: true }
      }));
    }

    message.success(`${type} applied successfully!`);
    setIsModalVisible(false);
    setCurrentTransformType(null);
    setTempSettings(null);
    setImageLoadError(null);
  };

  const handleCancelTransform = () => {
    setIsModalVisible(false);
    setCurrentTransformType(null);
    setTempSettings(null);
    setImageLoadError(null);
  };

  const drawAnnotations = () => {
  const canvas = canvasRef.current;
  const image = imageRef.current;
  if (!canvas || !image || !selectedProject) {
    console.warn('Cannot draw annotations:', { canvas, image, selectedProject });
    return;
  }

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const currentImageUrl = selectedProject.images[currentImageIndex]?.imageUrl || selectedProject.images[currentImageIndex];
  const currentImageAnnotations = selectedProject.annotations.find(
    img => img.imageUrl === currentImageUrl
  )?.annotations || [];

  currentImageAnnotations.forEach((annotation, index) => {
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    let labelX = annotation.x;
    let labelY = annotation.y;

    switch (annotation.type) {
      case 'rectangle':
        ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);
        break;

      case 'circle':
        if (annotation.radius) {
          ctx.beginPath();
          ctx.arc(annotation.x, annotation.y, annotation.radius, 0, 2 * Math.PI);
          ctx.stroke();
          labelX = annotation.x - annotation.radius;
          labelY = annotation.y - annotation.radius;
        }
        break;

      case 'polygon':
      case 'polyline':
        if (annotation.points && annotation.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

          // Draw all intermediate points
          for (let i = 1; i < annotation.points.length; i++) {
            ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
          }

          // For polyline: explicitly draw closing line to first point
          if (annotation.type === 'polyline') {
            ctx.lineTo(annotation.points[0].x, annotation.points[0].y);
          } else {
            ctx.closePath(); // For polygon only
          }

          ctx.stroke();

          // Label at first point
          labelX = annotation.points[0].x;
          labelY = annotation.points[0].y;
        }
        break;

      default:
        break;
    }

    // Draw label
    const label = annotation.class || `Region ${index + 1}`;
    ctx.font = '12px Inter, system-ui, sans-serif';
    const textMetrics = ctx.measureText(label);
    const padding = 6;
    const labelWidth = textMetrics.width + padding * 2;
    const labelHeight = 20;

    ctx.fillStyle = '#3b82f6';
    ctx.fillRect(labelX, labelY - labelHeight, labelWidth, labelHeight);
    ctx.fillStyle = 'white';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, labelX + padding, labelY - labelHeight / 2);
  });
};


  const getValidImageUrl = (image) => {
    const url = image?.imageUrl || image;
    if (!url || typeof url !== 'string' || !url.startsWith('http')) {
      console.warn('Invalid image URL:', url);
      return null;
    }
    return url;
  };

  const loadImageWithRetry = (url, retries = 2) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        if (retries > 0) {
          setTimeout(() => {
            loadImageWithRetry(url, retries - 1).then(resolve).catch(reject);
          }, 1000);
        } else {
          console.error('Failed to load image after retries:', url);
          reject(new Error('Failed to load image'));
        }
      };
    });
  };

  useEffect(() => {
    if (!isModalVisible || !tempSettings || !currentTransformType || !selectedProject) {
      console.warn('useEffect skipped: missing required states', {
        isModalVisible,
        tempSettings,
        currentTransformType,
        selectedProject
      });
      return;
    }

    const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
    if (!imageUrl) {
      console.error('No valid image URL found for index:', currentImageIndex);
      setImageLoadError('Invalid image URL');
      message.error('Failed to load image for preview. Please check the image URL.');
      return;
    }

    let isMounted = true;

    loadImageWithRetry(imageUrl)
      .then((img) => {
        if (isMounted && previewCanvasRef.current) {
          applyPreviewEffect(previewCanvasRef.current, img, currentTransformType.type, tempSettings);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setImageLoadError(err.message);
          message.error('Failed to load image. Please check the image URL or server.');
        }
      });

    return () => {
      isMounted = false; // Cleanup to prevent updates on unmounted component
    };
  }, [isModalVisible, tempSettings, currentTransformType, currentImageIndex, selectedProject]);

  const applyPreview = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      message.success('Preview updated successfully!');
    }, 1500);
  };

 const estimateOutputImages = () => {
  const baseImages = selectedProject?.images?.length || 0;

  // 🔹 Preprocessing count (excluding original)
  let preprocessingCount = 0;

  if (preprocessing.resizing?.enabled) preprocessingCount++;
  if (preprocessing.grayscale?.enabled) preprocessingCount++;
  if (preprocessing.autoEdgeContract?.enabled) preprocessingCount++;

  // 🔹 Augmentation count (excluding base)
  let augmentationCount = 0;

  if (augmentation.flip?.enabled) {
    if (augmentation.flip.horizontal) augmentationCount++;
    if (augmentation.flip.vertical) augmentationCount++;
  }

  const singleAugList = [
    "crop",
    "rotation",
    "blur",
    "grayscale",
    "saturation",
    "brightness",
    "exposure",
    "noise"
  ];

  singleAugList.forEach((key) => {
    if (augmentation[key]?.enabled) {
      augmentationCount++;
    }
  });

  if (augmentation.shear?.enabled) {
    augmentationCount += 4;
  }

  // ✅ FINAL (ADDITIVE)
  return baseImages * (1 + preprocessingCount + augmentationCount);
};

  const createVersion = async () => {
  
    if (!selectedProject?.projectName || !selectedProject?.images || !selectedProject?.annotations) {
      message.error('Invalid project data. Please ensure a valid project is selected.');
      return;
    }

    const estimatedImages = estimateOutputImages();
    const requiredCredits = Math.ceil(estimatedImages / 10);

    // ✅ CREDIT CHECK
    if (requiredCredits > user.credits) {
      return Modal.warning({
        centered: true,
        width: 520,
        icon: null,
        okText: 'Got it',
        title: 'Insufficient Credits',
        content: (
          <div className="space-y-4">

            <div className="p-4 bg-gray-50 rounded-xl border space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Base Images</span>
                <span>{selectedProject.images.length}</span>
              </div>

              <div className="flex justify-between">
                <span>Estimated Output Images</span>
                <span>{estimatedImages}</span>
              </div>

              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Credits Required</span>
                <span className="text-red-600 font-bold">{requiredCredits}</span>
              </div>

              <div className="flex justify-between">
                <span>Available Credits</span>
                <span className={`font-bold ${
                  user.credits < requiredCredits ? 'text-red-600' : 'text-green-600'
                }`}>
                  {user.credits}
                </span>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              1 credit is used for every 10 generated images.
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              💡 Reduce augmentation probability or steps to lower credit usage.
            </div>

          </div>
        ),
      });
    }
  
    setIsProcessing(true);
    setProgress(0);
  
    try {
      const payload = {
        username: user.username,
        organizationId,
        versionDescription,
        projectName: selectedProject.projectName,
        originalImages: selectedProject.images,
        originalAnnotations: selectedProject.annotations,
        preprocessing,
        augmentation,
        exportConfig: {},
      };
    
      let buffer = "";
  
      await axios.post("dataset-version", payload, {
        timeout: 1200000,
        responseType: "text",
        onDownloadProgress: (event) => {
          try {
            // Accumulate chunks
            buffer += event.event.target.responseText;
  
            // Split by newline and parse each JSON line
            const lines = buffer.split("\n");
            buffer = lines.pop(); // keep incomplete last line
  
            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed) continue;
  
              const data = JSON.parse(trimmed);
  
              if (data.type === "progress") {
                setProgress(data.progress);
              }
  
              if (data.type === "complete") {
                setProgress(100);
                setIsProcessing(false);
                message.success("Dataset version created successfully!");
                navigate('/sidebar/Viewdatasetversion');
              }
  
              if (data.type === "error") {
                console.error("❌ Error:", data.message);
                setIsProcessing(false);
                message.error(data.message || "Failed to create dataset version");
              }
            }
          } catch (parseErr) {
            // Incomplete chunk — will be retried on next event
          }
        },
      });
  
    } catch (err) {
      console.error('❌ Error creating dataset version:', err);
      setIsProcessing(false);
  
      if (err.response) {
        message.error(err.response.data?.message || `Failed (Status: ${err.response.status})`);
      } else if (err.request) {
        message.error('Network error. Please check your connection.');
      } else {
        message.error('Something went wrong while creating dataset version');
      }
    }
  };

  const PreprocessingCard = ({ title, icon: Icon, transformType, children }) => (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <Switch
            checked={preprocessing[transformType]?.enabled}
            onChange={(checked) => {
              if (checked) {
                openPreviewModal(transformType, 'preprocessing', preprocessing[transformType]);
              } else {
                setPreprocessing(prev => ({
                  ...prev,
                  [transformType]: { ...prev[transformType], enabled: false }
                }));
              }
            }}
          />
        </div>
      </div>
      {preprocessing[transformType]?.enabled && (
        <div className="p-4">
          {children}
        </div>
      )}
    </div>
  );

  const AugmentationCard = ({ title, icon: Icon, transformType, children }) => (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <Switch
            checked={augmentation[transformType]?.enabled}
            onChange={(checked) => {
              if (checked) {
                openPreviewModal(transformType, 'augmentation', augmentation[transformType]);
              } else {
                setAugmentation(prev => ({
                  ...prev,
                  [transformType]: { ...prev[transformType], enabled: false }
                }));
              }
            }}
          />
        </div>
      </div>
      {augmentation[transformType]?.enabled && (
        <div className="p-4">
          {children}
        </div>
      )}
    </div>
  );

  const ImageMultiplicationBreakdown = ({ selectedProject, preprocessing, augmentation }) => {
    const baseImages = selectedProject?.images?.length || 0;

    // Count preprocessing steps
    const preSteps = [];
    if (preprocessing.resizing?.enabled)         preSteps.push({ label: 'Image resizing', color: 'green' });
    if (preprocessing.grayscale?.enabled)        preSteps.push({ label: 'Grayscale conversion', color: 'green' });
    if (preprocessing.autoEdgeContract?.enabled) preSteps.push({ label: 'Auto edge contrast', color: 'green' });

    // Count augmentation steps (shear = 4)
    const augSteps = [];
    if (augmentation.flip?.enabled) {
      if (augmentation.flip.horizontal) augSteps.push({ label: 'Horizontal flip', color: 'purple', mult: 1 });
      if (augmentation.flip.vertical)   augSteps.push({ label: 'Vertical flip', color: 'purple', mult: 1 });
    }
    const singleAugs = ['crop', 'rotation', 'blur', 'grayscale', 'saturation', 'brightness', 'exposure', 'noise'];
    singleAugs.forEach(key => {
      if (augmentation[key]?.enabled) {
        const labels = {
          crop: 'Random crop', rotation: 'Rotation', blur: 'Blur',
          grayscale: 'Grayscale aug', saturation: 'Saturation',
          brightness: 'Brightness', exposure: 'Exposure', noise: 'Noise'
        };
        augSteps.push({ label: labels[key], color: 'purple', mult: 1 });
      }
    });
    if (augmentation.shear?.enabled) {
      augSteps.push({ label: 'Shear (4 variants)', color: 'purple', mult: 4 });
    }

    const preCount  = preSteps.length;
    const augCount  = augSteps.reduce((s, a) => s + (a.mult || 1), 0);
    const multiplier = 1 + preCount + augCount;
    const total      = baseImages * multiplier;

    const badgeStyle = (color) => ({
      green:  { background: '#EAF3DE', color: '#27500A' },
      purple: { background: '#EEEDFE', color: '#3C3489' },
      blue:   { background: '#E6F1FB', color: '#0C447C' },
    }[color] || {});

    const dotColor = { green: '#639922', purple: '#7F77DD', blue: '#185FA5' };

    return (
      <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 border border-slate-200/60">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
          <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-600" />
          Output Image Estimate
        </h3>

        {/* Three metric cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: 'Base images', value: baseImages, highlight: false },
            { label: 'Multiplier',  value: `×${multiplier}`, highlight: false },
            { label: 'Total output', value: total, highlight: true },
          ].map(({ label, value, highlight }) => (
            <div
              key={label}
              className={`rounded-lg p-3 ${highlight ? 'bg-blue-50' : 'bg-slate-50'}`}
            >
              <p className={`text-xs mb-1 ${highlight ? 'text-blue-600' : 'text-slate-500'}`}>{label}</p>
              <p className={`text-xl font-semibold ${highlight ? 'text-blue-700' : 'text-slate-800'}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Formula chips */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3 text-sm">
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{baseImages} imgs</span>
          <span className="text-slate-400 font-medium">×</span>
          <span className="px-2 py-0.5 rounded text-xs" style={badgeStyle('blue')}>1 original</span>
          {preCount > 0 && (
            <>
              <span className="text-slate-400">+</span>
              <span className="px-2 py-0.5 rounded text-xs" style={badgeStyle('green')}>{preCount} pre</span>
            </>
          )}
          {augCount > 0 && (
            <>
              <span className="text-slate-400">+</span>
              <span className="px-2 py-0.5 rounded text-xs" style={badgeStyle('purple')}>{augCount} aug</span>
            </>
          )}
          <span className="text-slate-400 font-medium">=</span>
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={badgeStyle('blue')}>{total} imgs</span>
        </div>

        {/* Breakdown rows */}
        <div className="border-t border-slate-100 pt-3 space-y-0">
          {/* Base row */}
          <div className="flex items-center gap-2 py-1.5 border-b border-slate-50 text-xs">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor.blue }} />
            <span className="flex-1 text-slate-700">Original (base)</span>
            <span className="px-1.5 py-0.5 rounded text-xs" style={badgeStyle('blue')}>×1</span>
            <span className="text-slate-500 w-14 text-right">{baseImages} imgs</span>
          </div>

          {/* Preprocessing rows */}
          {preSteps.map((step, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 text-xs">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor.green }} />
              <span className="flex-1 text-slate-700">{step.label}</span>
              <span className="px-1.5 py-0.5 rounded text-xs" style={badgeStyle('green')}>×1</span>
              <span className="text-slate-500 w-14 text-right">{baseImages} imgs</span>
            </div>
          ))}

          {/* Augmentation rows */}
          {augSteps.map((step, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 text-xs">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor.purple }} />
              <span className="flex-1 text-slate-700">{step.label}</span>
              <span className="px-1.5 py-0.5 rounded text-xs" style={badgeStyle('purple')}>×{step.mult}</span>
              <span className="text-slate-500 w-14 text-right">{baseImages * step.mult} imgs</span>
            </div>
          ))}

          {/* Total row */}
          <div className="flex items-center justify-between pt-2 mt-1">
            <span className="text-xs font-medium text-slate-600">Estimated total</span>
            <span className="text-base font-bold text-blue-600">{total} images</span>
          </div>
        </div>

        {/* Credits hint */}
        <div className="mt-3 p-2.5 bg-amber-50 border border-amber-100 rounded-lg flex gap-2 text-xs text-amber-700">
          <span>💡</span>
          <span>Credits required: <strong>{Math.ceil(total / 10)}</strong> (1 per 10 images). You have <strong>{JSON.parse(localStorage.getItem('user'))?.credits || 0}</strong>.</span>
        </div>
      </div>
    );
  };

  const hasAnyTransformationEnabled =
    Object.values(preprocessing).some(p => p.enabled) ||
    Object.values(augmentation).some(a => a.enabled);


  if (!selectedProject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-600 mb-2">No Project Selected</h2>
          <p className="text-slate-500 mb-4">Please select a project to create a dataset version</p>
          <button
            onClick={() => navigate('/sidebar/ViewAnnotation')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Annotations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Preview Modal */}
      <Modal
        open={isModalVisible}
        onCancel={handleCancelTransform}
        footer={null}
        width={1200}
        centered
        className="preview-modal"
      >
        <div className="p-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
            <Eye className="w-6 h-6 mr-2 text-blue-600" />
            Preview Transformation
          </h2>

          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Original Image */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-700 flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" />
                Original Image
              </h3>
              <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center" style={{ minHeight: '400px' }}>
                {selectedProject?.images[currentImageIndex] ? (
                  <img
                    src={getValidImageUrl(selectedProject.images[currentImageIndex]) || ''}
                    alt="Original"
                    className="max-w-full max-h-96 object-contain"
                    onError={() => console.error('Original image failed to load')}
                  />
                ) : (
                  <p className="text-red-500">No image available</p>
                )}
              </div>
            </div>

            {/* Preview Image */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-700 flex items-center">
                <RefreshCw className="w-5 h-5 mr-2 text-emerald-600" />
                After Transformation
              </h3>
              <div className="border-2 border-emerald-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center" style={{ minHeight: '400px' }}>
                {imageLoadError ? (
                  <p className="text-red-500">{imageLoadError}</p>
                ) : (
                  <canvas
                    ref={previewCanvasRef}
                    className="max-w-full max-h-96"
                    style={{ display: 'block' }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Settings Panel */}
          {tempSettings && currentTransformType && (
            <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
              <h3 className="font-semibold text-slate-800 mb-4">Adjust Settings</h3>
              
              {currentTransformType.type === 'flip' && (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Horizontal Flip</label>
                    <Switch
                      checked={tempSettings.horizontal}
                      onChange={(checked) => {
                        const newSettings = { ...tempSettings, horizontal: checked };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for flip transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'flip', newSettings);
                            }
                          })
                          .catch((err) => {
                            console.error('Failed to load image for flip:', imageUrl);
                            setImageLoadError(err.message);
                          });
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Vertical Flip</label>
                    <Switch
                      checked={tempSettings.vertical}
                      onChange={(checked) => {
                        const newSettings = { ...tempSettings, vertical: checked };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for flip transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'flip', newSettings);
                            }
                          })
                          .catch((err) => {
                            console.error('Failed to load image for flip:', imageUrl);
                            setImageLoadError(err.message);
                          });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Probability: {tempSettings.probability}
                    </label>
                    <Slider
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      value={tempSettings.probability}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, probability: value };
                        setTempSettings(newSettings);
                      }}
                      marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                    />
                  </div>
                </>
              )}

              {currentTransformType.type === 'rotation' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Rotation Angle: {tempSettings.angle}°
                    </label>
                    <Slider
                      min={0}
                      max={360}
                      value={tempSettings.angle}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, angle: value };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for rotation transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'rotation', newSettings);
                            }
                          })
                          .catch((err) => {
                            console.error('Failed to load image for rotation:', imageUrl);
                            setImageLoadError(err.message);
                          });
                      }}
                      marks={{ 0: '0°', 90: '90°', 180: '180°', 270: '270°', 360: '360°' }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Random Direction</label>
                    <Switch
                      checked={tempSettings.random}
                      onChange={(checked) => {
                        const newSettings = { ...tempSettings, random: checked };
                        setTempSettings(newSettings);
                      }}
                    />
                  </div>
                </>
              )}

              {currentTransformType.type === 'crop' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Crop Size: {Math.round(tempSettings.size * 100)}%
                    </label>
                    <Slider
                      min={0.3}
                      max={1.0}
                      step={0.05}
                      value={tempSettings.size}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, size: value };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for crop transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'crop', newSettings);
                            }
                          })
                          .catch((err) => {
                            console.error('Failed to load image for crop:', imageUrl);
                            setImageLoadError(err.message);
                          });
                      }}
                      marks={{ 0.3: '30%', 0.5: '50%', 0.75: '75%', 1.0: '100%' }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Random Position</label>
                    <Switch
                      checked={tempSettings.random}
                      onChange={(checked) => {
                        const newSettings = { ...tempSettings, random: checked };
                        setTempSettings(newSettings);
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Aspect Ratio</label>
                    <Select
                      value={tempSettings.aspectRatio}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, aspectRatio: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    >
                      <Option value="preserve">Preserve Original</Option>
                      <Option value="square">Square (1:1)</Option>
                      <Option value="wide">Wide (16:9)</Option>
                      <Option value="portrait">Portrait (4:3)</Option>
                    </Select>
                  </div>
                </>
              )}

              {currentTransformType.type === 'brightness' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Brightness Range: {tempSettings.range[0]} - {tempSettings.range[1]}
                  </label>
                  <Slider
                    range
                    min={0.1}
                    max={2.0}
                    step={0.1}
                    value={tempSettings.range}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, range: value };
                      setTempSettings(newSettings);
                      const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                      if (!imageUrl) {
                        console.error('No valid image URL for brightness transformation');
                        setImageLoadError('Invalid image URL');
                        return;
                      }
                      loadImageWithRetry(imageUrl)
                        .then((img) => {
                          if (previewCanvasRef.current) {
                            applyPreviewEffect(previewCanvasRef.current, img, 'brightness', newSettings);
                          }
                        })
                        .catch((err) => {
                          console.error('Failed to load image for brightness:', imageUrl);
                          setImageLoadError(err.message);
                        });
                    }}
                    marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                  />
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">
                    Probability: {tempSettings.probability}
                  </label>
                  <Slider
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    value={tempSettings.probability}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, probability: value };
                      setTempSettings(newSettings);
                    }}
                    marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                  />
                </div>
              )}

              {currentTransformType.type === 'saturation' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Saturation Range: {tempSettings.range[0]} - {tempSettings.range[1]}
                  </label>
                  <Slider
                    range
                    min={0.1}
                    max={2.0}
                    step={0.1}
                    value={tempSettings.range}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, range: value };
                      setTempSettings(newSettings);
                      const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                      if (!imageUrl) {
                        console.error('No valid image URL for saturation transformation');
                        setImageLoadError('Invalid image URL');
                        return;
                      }
                      loadImageWithRetry(imageUrl)
                        .then((img) => {
                          if (previewCanvasRef.current) {
                            applyPreviewEffect(previewCanvasRef.current, img, 'saturation', newSettings);
                          }
                        })
                        .catch((err) => {
                          console.error('Failed to load image for saturation:', imageUrl);
                          setImageLoadError(err.message);
                        });
                    }}
                    marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                  />
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">
                    Probability: {tempSettings.probability}
                  </label>
                  <Slider
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    value={tempSettings.probability}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, probability: value };
                      setTempSettings(newSettings);
                    }}
                    marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                  />
                </div>
              )}

              {currentTransformType.type === 'blur' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                    <Select
                      value={tempSettings.type}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, type: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    >
                      <Option value="gaussian">Gaussian Blur</Option>
                      <Option value="motion">Motion Blur</Option>
                      <Option value="average">Average Blur</Option>
                      <Option value="median">Median Blur</Option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Blur Radius: {tempSettings.radius}px
                    </label>
                    <Slider
                      min={0.5}
                      max={10.0}
                      step={0.5}
                      value={tempSettings.radius}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, radius: value };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for blur transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'blur', newSettings);
                            }
                          })
                          .catch((err) => {
                            console.error('Failed to load image for blur:', imageUrl);
                            setImageLoadError(err.message);
                          });
                      }}
                      marks={{ 0.5: '0.5', 5: '5', 10: '10' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Probability: {tempSettings.probability}
                    </label>
                    <Slider
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      value={tempSettings.probability}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, probability: value };
                        setTempSettings(newSettings);
                      }}
                      marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                    />
                  </div>
                </>
              )}

              {currentTransformType.type === 'exposure' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Exposure Range: {tempSettings.range[0]} - {tempSettings.range[1]}
                  </label>
                  <Slider
                    range
                    min={0.1}
                    max={2.0}
                    step={0.1}
                    value={tempSettings.range}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, range: value };
                      setTempSettings(newSettings);
                      const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                      if (!imageUrl) {
                        console.error('No valid image URL for exposure transformation');
                        setImageLoadError('Invalid image URL');
                        return;
                      }
                      loadImageWithRetry(imageUrl)
                        .then((img) => {
                          if (previewCanvasRef.current) {
                            applyPreviewEffect(previewCanvasRef.current, img, 'exposure', newSettings);
                          }
                        })
                        .catch((err) => {
                          setImageLoadError(err.message);
                        });
                    }}
                    marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                  />
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">
                    Probability: {tempSettings.probability}
                  </label>
                  <Slider
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    value={tempSettings.probability}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, probability: value };
                      setTempSettings(newSettings);
                    }}
                    marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                  />
                </div>
              )}

              {currentTransformType.type === 'noise' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Intensity: {tempSettings.intensity}
                    </label>
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={tempSettings.intensity}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, intensity: value };
                        setTempSettings(newSettings);
                        const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                        if (!imageUrl) {
                          console.error('No valid image URL for noise transformation');
                          setImageLoadError('Invalid image URL');
                          return;
                        }
                        loadImageWithRetry(imageUrl)
                          .then((img) => {
                            if (previewCanvasRef.current) {
                              applyPreviewEffect(previewCanvasRef.current, img, 'noise', newSettings);
                            }
                          })
                          .catch((err) => {
                            setImageLoadError(err.message);
                          });
                      }}
                      marks={{ 1: '1', 5: '5', 10: '10' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Probability: {tempSettings.probability}
                    </label>
                    <Slider
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      value={tempSettings.probability}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, probability: value };
                        setTempSettings(newSettings);
                      }}
                      marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                    />
                  </div>
                </>
              )}

              {currentTransformType.type === 'resizing' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Width</label>
                      <InputNumber
                        min={32}
                        max={2048}
                        value={tempSettings.width}
                        onChange={(value) => {
                          const newSettings = { ...tempSettings, width: value };
                          setTempSettings(newSettings);
                          const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                          if (!imageUrl) return;
                          loadImageWithRetry(imageUrl)
                            .then((img) => {
                              if (previewCanvasRef.current) {
                                applyPreviewEffect(previewCanvasRef.current, img, 'resizing', newSettings);
                              }
                            })
                            .catch((err) => setImageLoadError(err.message));
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Height</label>
                      <InputNumber
                        min={32}
                        max={2048}
                        value={tempSettings.height}
                        onChange={(value) => {
                          const newSettings = { ...tempSettings, height: value };
                          setTempSettings(newSettings);
                          const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                          if (!imageUrl) return;
                          loadImageWithRetry(imageUrl)
                            .then((img) => {
                              if (previewCanvasRef.current) {
                                applyPreviewEffect(previewCanvasRef.current, img, 'resizing', newSettings);
                              }
                            })
                            .catch((err) => setImageLoadError(err.message));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </>
              )}

              {currentTransformType.type === 'staticCrop' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">X Position</label>
                    <InputNumber
                      min={0}
                      value={tempSettings.x}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, x: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Y Position</label>
                    <InputNumber
                      min={0}
                      value={tempSettings.y}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, y: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Width</label>
                    <InputNumber
                      min={1}
                      value={tempSettings.width}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, width: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Height</label>
                    <InputNumber
                      min={1}
                      value={tempSettings.height}
                      onChange={(value) => {
                        const newSettings = { ...tempSettings, height: value };
                        setTempSettings(newSettings);
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {currentTransformType.type === 'autoEdgeContract' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-700">Edge Enhancement Method</h3>
                  <Select
                    value={tempSettings.method}
                    onChange={(value) => {
                      const newSettings = { ...tempSettings, method: value };
                      // Set predefined parameters based on method
                      switch (value) {
                        case 'contrast_stretching':
                          newSettings.fixedSize = 400;
                          newSettings.strength = 1.5;
                          newSettings.threshold = 0.1;
                          break;
                        case 'histogram_equalization':
                          newSettings.fixedSize = 400;
                          newSettings.strength = 1.3;
                          newSettings.threshold = 0.15;
                          break;
                        case 'adaptive_equalization':
                          newSettings.fixedSize = 400;
                          newSettings.strength = 1.7;
                          newSettings.threshold = 0.2;
                          break;
                      }
                      setTempSettings(newSettings);
                      const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
                      if (!imageUrl) {
                        console.error('No valid image URL for autoEdgeContract');
                        setImageLoadError('Invalid image URL');
                        return;
                      }
                      loadImageWithRetry(imageUrl)
                        .then((img) => {
                          if (previewCanvasRef.current) {
                            applyPreviewEffect(previewCanvasRef.current, img, 'autoEdgeContract', newSettings);
                          }
                        })
                        .catch((err) => {
                          console.error('Failed to load image for autoEdgeContract:', imageUrl);
                          setImageLoadError(err.message);
                        });
                    }}
                    className="w-full"
                  >
                    <Option value="contrast_stretching">Contrast Stretching</Option>
                    <Option value="histogram_equalization">Histogram Equalization</Option>
                    <Option value="adaptive_equalization">Adaptive Equalization (CLAHE)</Option>
                  </Select>
                </div>
              )}

              {currentTransformType.type === 'shear' && (
  <>
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Horizontal: {tempSettings.horizontal}°
      </label>
      <Slider
        min={0}
        max={45}
        value={tempSettings.horizontal}
        onChange={(value) => {
          const newSettings = { ...tempSettings, horizontal: value };
          setTempSettings(newSettings);
          const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
          if (!imageUrl) return;
          loadImageWithRetry(imageUrl).then((img) => {
            if (previewCanvasRef.current)
              applyPreviewEffect(previewCanvasRef.current, img, 'shear', newSettings);
          });
        }}
        marks={{ 0: '0°', 15: '15°', 30: '30°', 45: '45°' }}
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Vertical: {tempSettings.vertical}°
      </label>
      <Slider
        min={0}
        max={45}
        value={tempSettings.vertical}
        onChange={(value) => {
          const newSettings = { ...tempSettings, vertical: value };
          setTempSettings(newSettings);
          const imageUrl = getValidImageUrl(selectedProject.images[currentImageIndex]);
          if (!imageUrl) return;
          loadImageWithRetry(imageUrl).then((img) => {
            if (previewCanvasRef.current)
              applyPreviewEffect(previewCanvasRef.current, img, 'shear', newSettings);
          });
        }}
        marks={{ 0: '0°', 15: '15°', 30: '30°', 45: '45°' }}
      />
    </div>
  </>
)}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancelTransform}
              className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleApplyTransform}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
            >
              <Check className="w-4 h-4 mr-2" />
              Apply Transformation
            </button>
          </div>
        </div>
      </Modal>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/sidebar/ViewAnnotation', { state: { selectedProject } })}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-0.5 transition-transform" />
                <span className="font-medium">Back to Annotations</span>
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-blue-600" />
                  Create Dataset Version
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Configure preprocessing and augmentation for {selectedProject.projectName}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={applyPreview}
                disabled={isProcessing}
                className="flex items-center px-4 py-2.5 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                Apply For All Images
              </button>
              <button
                onClick={createVersion}
                disabled={isProcessing || !hasAnyTransformationEnabled}
                className={`flex items-center px-6 py-2.5 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md
                ${hasAnyTransformationEnabled
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-slate-300 text-slate-500 cursor-not-allowed'}
                ${isProcessing ? 'opacity-50' : ''}`}              >
                {isProcessing ? (
                  <Spin size="small" className="mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Create Version
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left sidebar - Configuration */}
          <div className="xl:col-span-1 space-y-6">
            {/* Version Info */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 border border-slate-200/60">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-blue-600" />
                Version Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description (Optional)</label>
                  <textarea
                    value={versionDescription}
                    onChange={(e) => setVersionDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                    placeholder="Describe this version..."
                  />
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Base Project:</span>
                    <span className="font-medium">{selectedProject.projectName}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 mt-1">
                    <span>Images:</span>
                    <span className="font-medium">{selectedProject.images.length}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 mt-1">
                    <span>Annotations:</span>
                    <span className="font-medium">
                      {selectedProject.annotations.reduce((total, img) => total + (img.annotations?.length || 0), 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <ImageMultiplicationBreakdown
              selectedProject={selectedProject}
              preprocessing={preprocessing}
              augmentation={augmentation}
            />
          </div>

          {/* Main content area */}
          <div className="xl:col-span-2 space-y-8">
            {/* Image Preview */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60">
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200/60">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  Image Preview
                </h2>
                <div className="flex items-center space-x-3">
                  <select
                    value={currentImageIndex}
                    onChange={(e) => setCurrentImageIndex(parseInt(e.target.value))}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {selectedProject.images.map((_, index) => (
                      <option key={index} value={index}>
                        Image {index + 1}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-slate-500">
                    of {selectedProject.images.length}
                  </span>
                </div>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-slate-50/50 to-white">
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200/60 max-w-full mx-auto">
                  <img
  ref={imageRef}
  src={getValidImageUrl(selectedProject.images[currentImageIndex]) || ''}
  alt={`Preview image ${currentImageIndex + 1}`}
  className="max-w-full h-auto block"
  onError={() => console.error('Main preview image failed to load')}
  onLoad={() => {
    if (imageRef.current && canvasRef.current) {
      const img = imageRef.current;
      const canvas = canvasRef.current;

      // Set canvas to actual image pixel size
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Optional: Scale canvas display size to match rendered image
      const displayWidth = img.clientWidth;
      const displayHeight = img.clientHeight;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      // Redraw annotations
      drawAnnotations();
    }
  }}
/>
                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  />
                </div>
                
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
                    <Target className="w-3 h-3 mr-1" />
                    {selectedProject.annotations.find(img => img.imageUrl === getValidImageUrl(selectedProject.images[currentImageIndex]))?.annotations?.length || 0} annotations
                  </div>
                </div>
              </div>
            </div>

            {/* Preprocessing Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Sliders className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Preprocessing</h2>
                <p className="text-slate-500 text-sm">Clean and prepare data for training</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PreprocessingCard title="Image Resizing" icon={Maximize} transformType="resizing">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Width</label>
                        <InputNumber
                          min={32}
                          max={2048}
                          value={preprocessing.resizing.width}
                          onChange={(value) => setPreprocessing(prev => ({
                            ...prev,
                            resizing: { ...prev.resizing, width: value }
                          }))}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Height</label>
                        <InputNumber
                          min={32}
                          max={2048}
                          value={preprocessing.resizing.height}
                          onChange={(value) => setPreprocessing(prev => ({
                            ...prev,
                            resizing: { ...prev.resizing, height: value }
                          }))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </PreprocessingCard>

                <PreprocessingCard title="Grayscale Conversion" icon={Filter} transformType="grayscale">
                </PreprocessingCard>

                <PreprocessingCard title="Auto Edge Contract" icon={Contrast} transformType="autoEdgeContract">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Enhancement Method</label>
                      <Select
                        value={preprocessing.autoEdgeContract.method}
                        onChange={(value) => {
                          const newSettings = { ...preprocessing.autoEdgeContract, method: value };
                          // Set predefined parameters based on method
                          switch (value) {
                            case 'contrast_stretching':
                              newSettings.fixedSize = 400;
                              newSettings.strength = 1.5;
                              newSettings.threshold = 0.1;
                              break;
                            case 'histogram_equalization':
                              newSettings.fixedSize = 400;
                              newSettings.strength = 1.3;
                              newSettings.threshold = 0.15;
                              break;
                            case 'adaptive_equalization':
                              newSettings.fixedSize = 400;
                              newSettings.strength = 1.7;
                              newSettings.threshold = 0.2;
                              break;
                          }
                          setPreprocessing((prev) => ({
                            ...prev,
                            autoEdgeContract: newSettings,
                          }));
                        }}
                        className="w-full"
                      >
                        <Option value="contrast_stretching">Contrast Stretching</Option>
                        <Option value="histogram_equalization">Histogram Equalization</Option>
                        <Option value="adaptive_equalization">Adaptive Equalization (CLAHE)</Option>
                      </Select>
                    </div>
                  </div>
                </PreprocessingCard>
              </div>
            </div>

            {/* Augmentation Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Shuffle className="w-4 h-4 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Data Augmentation</h2>
                <p className="text-slate-500 text-sm">Increase data variety while preserving labels</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AugmentationCard title="Flip" icon={FlipHorizontal} transformType="flip">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-600">Horizontal Flip</label>
                      <Switch
                        size="small"
                        checked={augmentation.flip.horizontal}
                        onChange={(checked) => setAugmentation(prev => ({
                          ...prev,
                          flip: { ...prev.flip, horizontal: checked }
                        }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-600">Vertical Flip</label>
                      <Switch
                        size="small"
                        checked={augmentation.flip.vertical}
                        onChange={(checked) => setAugmentation(prev => ({
                          ...prev,
                          flip: { ...prev.flip, vertical: checked }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.flip.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.flip.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          flip: { ...prev.flip, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Random Crop" icon={Crop} transformType="crop">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Crop Size: {Math.round(augmentation.crop.size * 100)}%
                      </label>
                      <Slider
                        min={0.5}
                        max={1.0}
                        step={0.05}
                        value={augmentation.crop.size}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          crop: { ...prev.crop, size: value }
                        }))}
                        marks={{ 0.5: '50%', 0.75: '75%', 1.0: '100%' }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-600">Random Position</label>
                      <Switch
                        size="small"
                        checked={augmentation.crop.random}
                        onChange={(checked) => setAugmentation(prev => ({
                          ...prev,
                          crop: { ...prev.crop, random: checked }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Aspect Ratio</label>
                      <Select
                        value={augmentation.crop.aspectRatio}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          crop: { ...prev.crop, aspectRatio: value }
                        }))}
                        className="w-full"
                      >
                        <Option value="preserve">Preserve Original</Option>
                        <Option value="square">Square (1:1)</Option>
                        <Option value="wide">Wide (16:9)</Option>
                        <Option value="portrait">Portrait (4:3)</Option>
                      </Select>
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Rotation" icon={RotateCw} transformType="rotation">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Max Angle: {augmentation.rotation.angle}°
                      </label>
                      <Slider
                        min={0}
                        max={180}
                        value={augmentation.rotation.angle}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          rotation: { ...prev.rotation, angle: value }
                        }))}
                        marks={{ 0: '0°', 45: '45°', 90: '90°', 180: '180°' }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-600">Random Direction</label>
                      <Switch
                        size="small"
                        checked={augmentation.rotation.random}
                        onChange={(checked) => setAugmentation(prev => ({
                          ...prev,
                          rotation: { ...prev.rotation, random: checked }
                        }))}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Blur" icon={Droplet} transformType="blur">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                      <Select
                        value={augmentation.blur.type}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          blur: { ...prev.blur, type: value }
                        }))}
                        className="w-full"
                      >
                        <Option value="gaussian">Gaussian Blur</Option>
                        <Option value="motion">Motion Blur</Option>
                        <Option value="average">Average Blur</Option>
                        <Option value="median">Median Blur</Option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Radius: {augmentation.blur.radius}
                      </label>
                      <Slider
                        min={0.5}
                        max={5.0}
                        step={0.1}
                        value={augmentation.blur.radius}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          blur: { ...prev.blur, radius: value }
                        }))}
                        marks={{ 0.5: '0.5', 2.0: '2.0', 5.0: '5.0' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.blur.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.blur.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          blur: { ...prev.blur, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Grayscale" icon={Filter} transformType="grayscale">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.grayscale.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.grayscale.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          grayscale: { ...prev.grayscale, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Saturation" icon={Palette} transformType="saturation">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Range: {augmentation.saturation.range[0]} - {augmentation.saturation.range[1]}
                      </label>
                      <Slider
                        range
                        min={0.1}
                        max={2.0}
                        step={0.1}
                        value={augmentation.saturation.range}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          saturation: { ...prev.saturation, range: value }
                        }))}
                        marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.saturation.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.saturation.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          saturation: { ...prev.saturation, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Brightness" icon={Sun} transformType="brightness">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Range: {augmentation.brightness.range[0]} - {augmentation.brightness.range[1]}
                      </label>
                      <Slider
                        range
                        min={0.1}
                        max={2.0}
                        step={0.1}
                        value={augmentation.brightness.range}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          brightness: { ...prev.brightness, range: value }
                        }))}
                        marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.brightness.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.brightness.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          brightness: { ...prev.brightness, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Exposure" icon={Camera} transformType="exposure">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Range: {augmentation.exposure.range[0]} - {augmentation.exposure.range[1]}
                      </label>
                      <Slider
                        range
                        min={0.1}
                        max={2.0}
                        step={0.1}
                        value={augmentation.exposure.range}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          exposure: { ...prev.exposure, range: value }
                        }))}
                        marks={{ 0.5: '0.5x', 1.0: '1.0x', 1.5: '1.5x', 2.0: '2.0x' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.exposure.probability}
                      </label>
                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.exposure.probability}
                        onChange={(value) => setAugmentation(prev => ({
                          ...prev,
                          exposure: { ...prev.exposure, probability: value }
                        }))}
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>
                  </div>
                </AugmentationCard>

                <AugmentationCard title="Shear" icon={Scissors} transformType="shear">
                  <div className="space-y-3">

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Horizontal: {augmentation.shear.horizontal}°
                      </label>

                      <Slider
                        min={0}
                        max={45}
                        value={augmentation.shear.horizontal}
                        onChange={(value) =>
                          setAugmentation(prev => ({
                            ...prev,
                            shear: { ...prev.shear, horizontal: value }
                          }))
                        }
                        marks={{ 0: '0°', 15: '15°', 30: '30°', 45: '45°' }}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Vertical: {augmentation.shear.vertical}°
                      </label>

                      <Slider
                        min={0}
                        max={45}
                        value={augmentation.shear.vertical}
                        onChange={(value) =>
                          setAugmentation(prev => ({
                            ...prev,
                            shear: { ...prev.shear, vertical: value }
                          }))
                        }
                        marks={{ 0: '0°', 15: '15°', 30: '30°', 45: '45°' }}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.shear.probability}
                      </label>

                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.shear.probability}
                        onChange={(value) =>
                          setAugmentation(prev => ({
                            ...prev,
                            shear: { ...prev.shear, probability: value }
                          }))
                        }
                        marks={{ 0.1: '0.1', 0.5: '0.5', 1.0: '1.0' }}
                      />
                    </div>

                  </div>
                </AugmentationCard>
                <AugmentationCard title="Noise" icon={Volume2} transformType="noise">
                  <div className="space-y-3">

                    {/* Intensity */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Intensity: {augmentation.noise.intensity}
                      </label>

                      <Slider
                        min={1}
                        max={10}
                        step={1}
                        value={augmentation.noise.intensity}
                        onChange={(value) =>
                          setAugmentation(prev => ({
                            ...prev,
                            noise: { ...prev.noise, intensity: value }
                          }))
                        }
                      />
                    </div>

                    {/* Probability */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Probability: {augmentation.noise.probability}
                      </label>

                      <Slider
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        value={augmentation.noise.probability}
                        onChange={(value) =>
                          setAugmentation(prev => ({
                            ...prev,
                            noise: { ...prev.noise, probability: value }
                          }))
                        }
                        marks={{
                          0.1: "0.1",
                          0.5: "0.5",
                          1.0: "1.0"
                        }}
                      />
                    </div>

                  </div>
                </AugmentationCard>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={isProcessing}
        footer={null}
        closable={false}
        centered
        maskClosable={false}

      >
        <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-2xl p-10 text-center">

          {/* Glow orbs */}
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-48 h-48 bg-indigo-100 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-6 right-4 w-32 h-32 bg-purple-100 rounded-full blur-2xl pointer-events-none" />

          {/* Spinning icon */}
          <div className="relative inline-flex items-center justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center shadow-[0_0_24px_rgba(99,102,241,0.15)] animate-pulse">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#818cf8" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="#a78bfa" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="#6366f1" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            </div>
            {/* Spinning ring */}
            <div className="absolute inset-[-6px] rounded-full border-2 border-transparent border-t-indigo-400 border-r-indigo-200 animate-spin" />
          </div>

          {/* Title */}
          <h2 className="text-lg font-bold tracking-tight text-slate-800 mb-1">
            Creating Dataset Version
          </h2>

          {/* Subtitle */}
          <p className="text-sm text-slate-400 mb-8 font-normal">
            Processing and indexing your data...
          </p>

          {/* Progress bar */}
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200 mb-3">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 shadow-[0_0_8px_rgba(139,92,246,0.4)] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="w-full h-full bg-white/30 animate-pulse rounded-full" />
            </div>
          </div>

          {/* Footer row */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">
              {progress < 40 ? '⚙ Preparing...' : progress < 80 ? '🔄 Indexing...' : '✓ Finalizing...'}
            </span>
            <span className="text-2xl font-extrabold tracking-tighter text-indigo-500">
              {progress}%
            </span>
          </div>

        </div>
      </Modal>
    </div>
  );
};

export default DatasetVersion;